"use client";

import React, { useState, useCallback, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FormValues } from "@/hooks/useAudioProcessing";
import { AudioUploadFormProps, VALID_EXTENSIONS } from "./types";
import { UploadStatus } from "..";
import { animationStyles } from "./styles";
import { FileDropArea } from "./FileDropArea";
import { SubmitButton } from "./SubmitButton";
import { StatusDisplay } from "./StatusDisplay";
import TabToggle, { TabMode } from "./TabToggle";
import { useAuth } from '@/contexts/AuthContext';
import { OptionTags } from "./OptionTags";
import type { OptionType } from "./OptionsPanel";
import AudioRecorder from "./AudioRecorder";
import { OptionsPanel } from "./OptionsPanel";
import { AudioPlayer } from "./AudioPlayer";

export const AudioUploadForm = ({ 
  onSubmit, 
  isSubmitting, 
  status, 
  audioUrl, 
  fileName,
  structureDefinitions,
  formats = [],
  isFormatsLoading = false
}: AudioUploadFormProps) => {
  const { user } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 初期値の取得（ローカルストレージから最後に使用された構造定義IDを取得）
  const getInitialStructureDefinitionId = useCallback(() => {
    try {
      const lastUsedId = localStorage.getItem('lastUsedStructureDefinitionId');
      console.log('🔍 ローカルストレージから取得した最後の構造定義ID:', lastUsedId);
      return lastUsedId || undefined;
    } catch (error) {
      console.warn('ローカルストレージアクセスエラー:', error);
      return undefined;
    }
  }, []);

  const { setValue, watch, getValues } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      email: user?.email || '',
      customPrompt: '',
      structureDefinitionId: getInitialStructureDefinitionId()
    }
  });
   
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [clickAnimation, setClickAnimation] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabMode>("upload");
  
  // オプション関連の状態
  const [isOptionsPanelOpen, setIsOptionsPanelOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  // フォームの値を監視
  const formValues = watch();

  // コンポーネントマウント時のアニメーション制御
  useEffect(() => {
    // マウント時にアニメーション開始
    setIsVisible(true);
  }, []);

  // 構造定義が取得されたタイミングで最新のIDを初期値として設定
  useEffect(() => {
    console.log('🔍 useEffect 構造定義ID設定 - 開始:', {
      structureDefinitions_length: structureDefinitions?.length || 0,
      current_structureDefinitionId: formValues.structureDefinitionId,
      current_structureDefinitionId_type: typeof formValues.structureDefinitionId
    });
    
    if (structureDefinitions && structureDefinitions.length > 0 && !formValues.structureDefinitionId) {
      // 作成日時で降順ソート（最新が最初）
      const sortedDefinitions = [...structureDefinitions].sort((a, b) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      const latestDefinitionId = sortedDefinitions[0]?.id;
      if (latestDefinitionId) {
        console.log('🔍 useEffect 構造定義ID設定 - setValue実行前:', {
          latestDefinitionId,
          sortedDefinitions: sortedDefinitions.map(d => ({ id: d.id, name: d.name }))
        });
        
        setValue('structureDefinitionId', latestDefinitionId);
        console.log('🔍 useEffect 構造定義ID設定 - setValue実行後:', latestDefinitionId);
      }
    } else {
      console.log('🔍 useEffect 構造定義ID設定 - スキップされた理由:', {
        hasStructureDefinitions: !!(structureDefinitions && structureDefinitions.length > 0),
        hasCurrentValue: !!formValues.structureDefinitionId
      });
    }
  }, [structureDefinitions, formValues.structureDefinitionId, setValue]);

  // フォームデータの取得
  const getFormData = useCallback(() => {
    // React Hook FormのgetValuesを使用してより確実に値を取得
    const currentValues = getValues();
    const data = {
      email: user?.email || '',
      file: fileInputRef.current?.files as FileList | null,
      customPrompt: currentValues.customPrompt || '',
      structureDefinitionId: currentValues.structureDefinitionId
    };
    
    // デバッグ用詳細ログ
    console.log('🔍 getFormData 詳細ログ:', {
      watch_formValues: formValues,
      getValues_currentValues: currentValues,
      final_structureDefinitionId: data.structureDefinitionId,
      final_structureDefinitionId_type: typeof data.structureDefinitionId,
      comparison: {
        watch_id: formValues.structureDefinitionId,
        getValues_id: currentValues.structureDefinitionId,
        areEqual: formValues.structureDefinitionId === currentValues.structureDefinitionId
      }
    });
    
    return data;
  }, [user?.email, fileInputRef, formValues, getValues]);

  // ボタンクリック時のアニメーション
  const handleButtonClick = useCallback(() => {
    setClickAnimation(true);
    setTimeout(() => setClickAnimation(false), 300);
  }, []);

  // オプション設定の判定
  const hasOptions = Boolean(
    formValues.customPrompt?.trim() || 
    formValues.structureDefinitionId
  );

  // オプションパネルの開閉
  const handleOptionsClick = useCallback(() => {
    setIsOptionsPanelOpen(prev => !prev);
    setSelectedOption(null);
  }, []);

  const handleOptionsClose = useCallback(() => {
    setIsOptionsPanelOpen(false);
    setSelectedOption(null);
  }, []);

  // プロンプト値変更ハンドラー
  const handlePromptChange = useCallback((value: string) => {
    setValue('customPrompt', value);
  }, [setValue]);

  // 構造定義変更ハンドラー
  const handleStructureDefinitionChange = useCallback((id: string) => {
    setValue('structureDefinitionId', id || undefined);
  }, [setValue]);

  // タグ削除ハンドラー
  const handleClearPrompt = useCallback(() => {
    setValue('customPrompt', '');
  }, [setValue]);

  const handleClearStructureDefinition = useCallback(() => {
    setValue('structureDefinitionId', undefined);
  }, [setValue]);

  // タブ切り替え
  const switchTab = useCallback((tab: TabMode) => {
    setActiveTab(tab);
  }, []);

  // アップロードプログレスのシミュレーション処理（イージング付き）
  const simulateUploadProgress = useCallback(() => {
    setUploadProgress(0);
    const startTime = Date.now();
    const duration = 750; // 750ms
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // より気持ちよいイージング（ExpoOut + 微細なElastic効果）
      let easedProgress;
      
      if (progress === 0) {
        easedProgress = 0;
      } else if (progress === 1) {
        easedProgress = 1;
      } else {
        // ExpoOut: 滑らかで劇的な減速
        const expoOut = 1 - Math.pow(2, -10 * progress);
        
        // 微細なElastic効果を最後に追加（より自然な感じ）
        const elasticFactor = progress > 0.8 ? 
          1 + (Math.sin(progress * Math.PI * 8) * 0.02 * (1 - progress)) : 0;
        
        easedProgress = expoOut + elasticFactor;
      }
      
      // 最小値を5%に設定して、初期段階でもプログレスバーが見えるようにする
      // 小数点以下も保持して滑らかなアニメーションにする
      const currentProgress = Math.max(5, Math.min(100, easedProgress * 100));
      
      setUploadProgress(currentProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setUploadProgress(100);
      }
    };
    
    requestAnimationFrame(animate);
  }, []);

  // 録音完了ハンドラー
  const handleRecordingComplete = useCallback((blob: Blob, fileName: string) => {
    // 録音ファイルをFileListと同様に扱うための処理
    const file = new File([blob], fileName, { type: blob.type });
    
    // DataTransferを使用してFileListを作成
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    
    if (fileInputRef.current) {
      // FileInputの値を設定
      fileInputRef.current.files = dataTransfer.files;
      
      // フォームの状態を更新
      setValue('file', dataTransfer.files);
      setSelectedFileName(fileName);
      
      // アップロードの進行状況を更新
      simulateUploadProgress();
    }
  }, [setValue, simulateUploadProgress]);

  // フォーム検証
  const validateForm = useCallback(() => {
    const formData = getFormData();
    const email = formData.email;
    const file = formData.file;
    
    // メールアドレスチェック（認証済みユーザーのため基本的には存在するはず）
    if (!email || email.trim() === '') {
      console.error('認証済みユーザーのメールアドレスが取得できません');
      return false;
    }
    
    // ファイルチェック
    if (!file || file.length === 0) {
      try {
        if (activeTab === "upload") {
          const fileLabel = document.querySelector('label[for="file-upload"]');
          fileLabel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            fileInputRef.current?.click();
          }, 300);
        } else {
          // 録音タブの場合は録音を促す
          switchTab("record");
        }
      } catch (error) {
        console.error("ファイル入力欄フォーカスエラー:", error);
      }
      return false;
    }
    
    return true;
  }, [getFormData, activeTab, switchTab]);

  // ファイル選択時の処理
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFileName(files[0].name);
      simulateUploadProgress();
    } else {
      setSelectedFileName("");
      setUploadProgress(0);
    }
  }, [simulateUploadProgress]);

  // ファイル削除ハンドラー
  const handleFileRemove = useCallback(() => {
    // ファイル入力をクリア
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      // 空のFileListを作成
      const dataTransfer = new DataTransfer();
      fileInputRef.current.files = dataTransfer.files;
    }
    
    // 状態をリセット
    setSelectedFileName("");
    setUploadProgress(0);
    setValue('file', new DataTransfer().files);
  }, [setValue]);

  // ドラッグ関連イベントハンドラー
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSubmitting && status.state !== "SAVING") {
      setIsDragging(true);
    }
  }, [isSubmitting, status.state]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSubmitting && status.state !== "SAVING") {
      setIsDragging(true);
    }
  }, [isSubmitting, status.state]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (isSubmitting || status.state === "SAVING") return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      
      if (VALID_EXTENSIONS.includes(fileExt) || 
          VALID_EXTENSIONS.some(ext => file.type.includes(ext))) {
        setSelectedFileName(file.name);
        setValue('file', files);
        
        // ファイル入力要素に直接設定
        if (fileInputRef.current) {
          // DataTransferオブジェクトをFileListに変換して設定
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInputRef.current.files = dataTransfer.files;
        }
        
        simulateUploadProgress();
      } else {
        alert(`非対応のファイル形式です。${VALID_EXTENSIONS.join(', ')}のいずれかをアップロードしてください。`);
      }
    }
  }, [isSubmitting, status.state, setValue, simulateUploadProgress]);

  // アップロード状態による判定関数
  const isDisabled = useCallback((state: UploadStatus["state"]) => {
    return state === "SAVING" || state === "SUCCESS";
  }, []);

  const isProcessing = useCallback((state: UploadStatus["state"]) => {
    return state === "SAVING";
  }, []);

  const isSuccess = useCallback((state: UploadStatus["state"]) => {
    return state === "SUCCESS";
  }, []);

  // アップロード状態に応じたスタイルとアイコンを決定
  const getUploadStatusStyles = useCallback(() => {
    if (isDragging) {
      return "border-primary border-dashed border-2 bg-primary/5 shadow-md transform transition-all duration-200";
    }
    
    if (selectedFileName) {
      return "border-primary border-solid border-2 bg-primary/5 shadow-md transform transition-all duration-200";
    }
    
    switch (status.state) {
      case "SUCCESS":
        return "border-success border-solid border-2 bg-success/5 shadow-md transform transition-all duration-200";
      case "ERROR":
        return "border-error border-solid border-2 bg-error/5 shadow-md transform transition-all duration-200";
      case "SAVING":
        return "border-primary border-solid border-2 bg-primary/5 shadow-md transform transition-all duration-200";
      case "PENDING":
      default:
        return "border-dashed border-2 border-gray-300 hover:border-primary/50 transition-all duration-200";
    }
  }, [isDragging, selectedFileName, status.state]);

  // カスタム送信ハンドラー
  const customSubmit = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (validateForm()) {
      const formData = getFormData();
      
      // structureDefinitionIdの処理（より詳細な条件チェック）
      let effectiveStructureDefinitionId = formData.structureDefinitionId;
      
      console.log('🔍 customSubmit 構造定義ID処理:', {
        initial_formData_structureDefinitionId: formData.structureDefinitionId,
        initial_type: typeof formData.structureDefinitionId,
        is_empty_string: formData.structureDefinitionId === '',
        is_undefined: formData.structureDefinitionId === undefined,
        is_null: formData.structureDefinitionId === null,
        structureDefinitions_available: !!(structureDefinitions && structureDefinitions.length > 0)
      });
      
      // undefinedまたは空文字の場合のみフォールバック処理を実行
      if ((!effectiveStructureDefinitionId || effectiveStructureDefinitionId === '') && structureDefinitions && structureDefinitions.length > 0) {
        // 作成日時で降順ソート（最新が最初）
        const sortedDefinitions = [...structureDefinitions].sort((a, b) => {
          if (!a.created_at && !b.created_at) return 0;
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        effectiveStructureDefinitionId = sortedDefinitions[0].id;
        console.log('⚠️  フォールバック処理実行 - 最新の構造定義IDを使用:', {
          fallback_id: effectiveStructureDefinitionId,
          fallback_name: sortedDefinitions[0].name,
          original_was: formData.structureDefinitionId,
          available_definitions: sortedDefinitions.map(d => ({ id: d.id, name: d.name }))
        });
      } else {
        console.log('✅ ユーザー指定の構造定義IDを使用:', effectiveStructureDefinitionId);
      }
      
      console.log('customSubmitで送信するformData:', {
        ...formData,
        structureDefinitionId: effectiveStructureDefinitionId
      });
      
      if (formData.email && formData.file) {
        onSubmit({
          email: formData.email,
          file: formData.file,
          customPrompt: formData.customPrompt,
          structureDefinitionId: effectiveStructureDefinitionId
        });
      }
    }
  }, [onSubmit, validateForm, getFormData, structureDefinitions]);

  // キーボードイベントハンドラー
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // カスタムプロンプトタブが開いているときは送信処理をスキップ
    if (isOptionsPanelOpen && selectedOption === 'custom-prompt') {
      return;
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleButtonClick();
      customSubmit();
    }
  }, [customSubmit, handleButtonClick, isOptionsPanelOpen, selectedOption]);

  // ファイルの選択ダイアログを開く
  const openFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ステータスに応じたコンテンツをレンダリング
  const renderContent = useCallback(() => {
    if (status.state === "SUCCESS" || status.state === "SAVING" || status.state === "ERROR") {
      return <StatusDisplay status={status} audioUrl={audioUrl} fileName={fileName} />;
    }

    if (status.state === "PENDING") {
      return (
        <div className={`${isVisible ? 'fade-in-scale' : 'opacity-0'} p-7 pt-8 relative`} style={{
          backdropFilter: 'blur(7px)',
          WebkitBackdropFilter: 'blur(7px)',
          borderRadius: '22px',
          border: 'var(--glass-border)',
          boxShadow: 'var(--glass-shadow)'
        }}>
          {/* タブ切り替えコンポーネント */}
          <TabToggle
            activeTab={activeTab}
            switchTab={switchTab}
          />

          {/* 選択されたタブに応じたコンテンツ表示 */}
          <div className="mt-4">
            {activeTab === "upload" ? (
              /* ファイルアップロードUI */
              <FileDropArea
                isDragging={isDragging}
                selectedFileName={selectedFileName}
                uploadProgress={uploadProgress}
                isSubmitting={isSubmitting}
                status={status}
                getUploadStatusStyles={getUploadStatusStyles}
                isProcessing={isProcessing}
                handleDragEnter={handleDragEnter}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                openFileSelector={openFileSelector}
                fileInputRef={fileInputRef}
                handleFileChange={handleFileChange}
                handleFileRemove={handleFileRemove}
              />
            ) : (
              /* 録音UI */
              <div className="fade-in">
                <AudioRecorder onRecordingComplete={handleRecordingComplete} />
              </div>
            )}
          </div>

          {/* 送信ボタンとオプション設定 */}
          <div className="mt-10">
            {/* オプションタグ表示エリア */}
            <OptionTags
              promptValue={formValues.customPrompt}
              selectedStructureDefinitionId={formValues.structureDefinitionId}
              structureDefinitions={structureDefinitions}
              formats={formats}
              onClearPrompt={handleClearPrompt}
              onClearStructureDefinition={handleClearStructureDefinition}
            />
            
            {/* 送信ボタンとオプションパネル */}
            <div className="relative">
              {/* オプション設定パネル */}
              <OptionsPanel
                isOpen={isOptionsPanelOpen}
                onClose={handleOptionsClose}
                selectedOption={selectedOption}
                onOptionSelect={setSelectedOption}
                promptValue={formValues.customPrompt || ''}
                onPromptChange={handlePromptChange}
                structureDefinitions={structureDefinitions}
                selectedStructureDefinitionId={formValues.structureDefinitionId}
                onStructureDefinitionChange={handleStructureDefinitionChange}
              />
              
              <SubmitButton
                selectedFileName={selectedFileName}
                isSubmitting={isSubmitting}
                clickAnimation={clickAnimation}
                status={status}
                uploadProgress={uploadProgress}
                handleButtonClick={handleButtonClick}
                customSubmit={customSubmit}
                isDisabled={isDisabled}
                isProcessing={isProcessing}
                isSuccess={isSuccess}
                onOptionsClick={handleOptionsClick}
                hasOptions={hasOptions}
                isOptionsOpen={isOptionsPanelOpen}
              />
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  }, [
    isVisible,
    status,
    audioUrl,
    fileName,
    selectedFileName,
    isDragging,
    uploadProgress,
    isSubmitting,
    activeTab,
    clickAnimation,
    switchTab,
    getUploadStatusStyles,
    isProcessing,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    openFileSelector,
    handleFileChange,
    handleButtonClick,
    customSubmit,
    isDisabled,
    isSuccess,
    handleRecordingComplete,
    handleOptionsClick,
    handleFileRemove,
    hasOptions,
    isOptionsPanelOpen,
    selectedOption,
    formValues.customPrompt,
    handlePromptChange,
    structureDefinitions,
    formValues.structureDefinitionId,
    handleStructureDefinitionChange,
    handleOptionsClose,
    handleClearPrompt,
    handleClearStructureDefinition
  ]);

  // メインのレンダリング
  return (
    <>
      {/* スタイルを適用 */}
      <style>{animationStyles}</style>
      
      <form 
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          customSubmit(e);
        }} 
        className="space-y-4 max-w-2xl min-w-md mx-auto" 
        onKeyDown={handleKeyDown}
      >
        {renderContent()}
        
        {/* アップロード後の音声プレーヤー表示（SUCCESS状態以外） */}
        {audioUrl && status.state !== "SUCCESS" && <AudioPlayer audioUrl={audioUrl} fileName={fileName} />}
      </form>
    </>
  );
}; 

export default AudioUploadForm;
